/**
 * Toast 非阻斷式通知元件
 *
 * 顯示短暫的通知訊息，不會阻斷使用者操作：
 * - 自動在指定時間後消失
 * - 支援不同類型（成功、錯誤、資訊）
 * - 從畫面右上角滑入
 */

// ========================================================================
// 型別定義
// ========================================================================

export type ToastType = 'success' | 'error' | 'info';

export interface ToastOptions {
  /** 顯示時間（毫秒），預設 2000ms */
  duration?: number;
  /** 通知類型，預設 'info' */
  type?: ToastType;
}

// ========================================================================
// 樣式常數
// ========================================================================

const TOAST_BASE_STYLES = [
  'fixed',
  'top-4',
  'right-4',
  'z-50',
  'px-4',
  'py-3',
  'rounded-lg',
  'shadow-lg',
  'text-sm',
  'font-medium',
  'transform',
  'transition-all',
  'duration-300',
  'ease-out',
].join(' ');

const TOAST_TYPE_STYLES: Record<ToastType, string> = {
  success: 'bg-green-500 text-white',
  error: 'bg-red-500 text-white',
  info: 'bg-gray-800 text-white',
};

// ========================================================================
// Toast 容器管理
// ========================================================================

let toastContainer: HTMLDivElement | null = null;

function getToastContainer(): HTMLDivElement {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'toast-container';
    toastContainer.className = 'fixed top-4 right-4 z-50 flex flex-col gap-2';
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
}

// ========================================================================
// Toast 函數
// ========================================================================

/**
 * 顯示 Toast 通知
 * @param message 通知訊息
 * @param options 選項（duration: 顯示時間, type: 類型）
 */
export function showToast(message: string, options: ToastOptions = {}): void {
  const { duration = 2000, type = 'info' } = options;

  const container = getToastContainer();

  // 建立 Toast 元素
  const toast = document.createElement('div');
  toast.className = `${TOAST_BASE_STYLES} ${TOAST_TYPE_STYLES[type]} translate-x-full opacity-0`;
  toast.textContent = message;

  // 加入容器
  container.appendChild(toast);

  // 觸發進入動畫（需要等待 DOM 更新）
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      toast.classList.remove('translate-x-full', 'opacity-0');
      toast.classList.add('translate-x-0', 'opacity-100');
    });
  });

  // 設定自動消失
  setTimeout(() => {
    // 觸發離開動畫
    toast.classList.remove('translate-x-0', 'opacity-100');
    toast.classList.add('translate-x-full', 'opacity-0');

    // 動畫結束後移除元素
    setTimeout(() => {
      toast.remove();
    }, 300); // 等待動畫完成
  }, duration);
}

/**
 * 顯示成功通知
 */
export function showSuccessToast(message: string, duration = 2000): void {
  showToast(message, { type: 'success', duration });
}

/**
 * 顯示錯誤通知
 */
export function showErrorToast(message: string, duration = 3000): void {
  showToast(message, { type: 'error', duration });
}

/**
 * 顯示資訊通知
 */
export function showInfoToast(message: string, duration = 2000): void {
  showToast(message, { type: 'info', duration });
}
